import React, { useEffect, useState } from "react";
import { QrReader } from "react-qr-reader";
import "./styles.scss";
import QRScannerDenied from "./../assets/qr-scanner-denied.png";
import Frame from "./../assets/frame.svg";
import { APIS } from "../api";
import { SetCookie } from "../../teacherDashboard/teacher/config/common";
import QRcodePopup from "./QRcodePopup";
import { useAuthSigninState } from "../state";
import { pushEventToGTM } from "../../commonHelpers/pushEventToGTM";
import { SwitchCamera, VideoFacingMode } from "./switchCamera";

let LiveStream: any = null;
let shouldReload = true;

function isShortURL(inputString: string) {
  // Regular expression to match URLs  includes "slearn.me"
  const urlPattern = /^.*slearn\.me.*/;

  return urlPattern.test(inputString);
}

function isURL(inputString: string): boolean {
  if (!inputString) return false;
  try {
    new URL(inputString); // If valid, it won't throw an error
    return true;
  } catch {
    return false; // If invalid, it throws an error
  }
}

export const QRcodeInput = ({setHasCameraAccess = (val: boolean) => {}}: any) => {
  const { showQRPopup, setShowQRPopup, getFromPage } = useAuthSigninState()

  const [result, setResult] = useState("");
  const [scannerError, setScannerError] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [success, setSuccess] = useState(null);

  const handleScan = (data: any, err: any) => {
    shouldReload = false;
    if (data) {
      setResult(data.text);
      if (data?.text) {
        setLoading(true);
        setSuccess(null);
        setError(null);
        setShowQRPopup(false)
        trackEventsForGuests("Class Login Attempted", { "mp_login_user_type": "student", "Login Method": "QR code" , ...(data?.text && isURL(data?.text) && { "Resolved URL": data?.text })}, { send_mixpanel: true })
        if(isShortURL(data?.text)) {
          SetCookie('qr_code_login_flow', "true", 1000*60*60*24*365);
          // [AI GENERATED CODE] — Set pending source before redirect so createSession picks up correct userId on next page.
          if (typeof (window as any).MUS !== 'undefined') (window as any).MUS.setPendingSource('qr_code');
          window.location.href = data?.text;
          return;
        }
        APIS.qrCodeLogin(data?.text).then((res) => {
          pushEventToGTM('logged_in', {user_type: 'student', label: 'qr code login'})
          setSuccess("Great job!");
          setTimeout(() => setLoading(false), 3000)
          // [AI GENERATED CODE] — Set pending source before navigation so createSession picks up correct userId on next page.
          if (typeof (window as any).MUS !== 'undefined') (window as any).MUS.setPendingSource('qr_code');
          window.location.href = `${res?.data?.redirect_to}?loginMethod=QR code&fromPage=QR code scan input screen`;
        }).catch((e) => {
          setError(e?.response?.data?.error || "Oops, try again!");
          setShowQRPopup(true)
        });
      }
    }
    if (err?.name === "NotAllowedError") {
      setScannerError(err);
    }
  };


  useEffect(() => {
    onRequestCamera(true);

    return () => {
      if (LiveStream) {
        LiveStream?.getTracks()?.forEach(function (track: any) {
          track?.stop();
          track.enabled = false;
        });
      }

      if (shouldReload) {
        trackAnalyticsEvent('Viewed QR Code Screen Ignored Camera Access', { 'Intent for': 'login', 'Screen type': 'page', 'From Page': 'signin' }, true);
        setTimeout(() => window.location.reload(), 0);
      }
    }
  }, [])

  const onRequestCamera = (sendEvent?: boolean, showAlert?: boolean) => {
    shouldReload = true;
    const constraints = {
      audio: false,
      video: { facingMode: VideoFacingMode.environment },
    };
    setLoading(true);
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        shouldReload = false;
        LiveStream = stream;
        setLoading(false);
        setScannerError(null);
        setHasCameraAccess(true);
        if(sendEvent) {
          trackEventsForGuests("Viewed QR Code Input Screen Camera Access", { "mp_login_user_type": "student", "From page": getFromPage() || "Direct URL entered"  }, { send_mixpanel: true })
          pushEventToGTM('viewed_qr_code_screen_camera_access', {'Intent for': 'login', 'Screen type': 'page', 'From Page': 'signin'})
        }
      })
      .catch((e) => {
        shouldReload = false;
        setLoading(false);
        if(sendEvent) {
          trackEventsForGuests("Viewed QR Code Input Screen No Camera Access", { "mp_login_user_type": "student", "From page": getFromPage() || "Direct URL entered"  }, { send_mixpanel: true })
          pushEventToGTM('viewed_qr_code_screen_no_camera_access', {'Intent for': 'login', 'Screen type': 'page', 'From Page': 'signin'})
        }
        setScannerError(e);
        setHasCameraAccess(false);
        if (showAlert) {
          setTimeout(() => {
            alert(
              "Your camera access is blocked. You need to manually provide permission and reload page."
            );
          }, 0)
        }
      });
  };

  return (
    <SwitchCamera>
      {
        ({ switchCamera, currentDeviceId, deviceCount, facingMode }: any) => (
          <>
            <div className="qr-code-input">
              {scannerError && (
                <div className="qr-scanner-denied">
                  <img src={QRScannerDenied} alt="Camera access denied" />
                  <p>
                    <i className="iconcust-iconWarning" />
                    <span>Allow camera access</span>
                  </p>
                </div>
              )}
              {isLoading && !scannerError && (
                <div className="qr-scanner-denied">
                  <img src={QRScannerDenied} alt="Loading camera" />
                  {
                    shouldReload &&
                    <p>
                      <i className="iconcust-iconWarning" />
                      <span>Allow camera access</span>
                    </p>
                  }
                </div>
              )}
              {!scannerError && !isLoading && (
                <div>
                  {
                    currentDeviceId ?
                    <QrReader
                      key={currentDeviceId}
                      className="qr-reader"
                      scanDelay={300}
                      onResult={handleScan}
                      constraints={{facingMode, deviceId: currentDeviceId }}
                    /> :
                    <QrReader
                      key={facingMode}
                      className="qr-reader"
                      scanDelay={300}
                      onResult={handleScan}
                      constraints={{ facingMode }}
                    />
                  }
                  <div className={`frame ${success ? "success" : ""}`}>
                    <Frame />
                    <div className="scanner"></div>
                  </div>
                </div>
              )}
            </div>
            <div className="info">
              {isLoading && !error && !success && result && (
                <div className="loading">Checking your QR code…</div>
              )}
              {error && <div className="error">{error}</div>}
              {success && <div className="success">{success}</div>}
            </div>
            <div className="info-capture-msg">The process of scanning the QR code does not capture any image, video, or other personal information. The process only reads the information contained in the QR code, which does not include any personal information.</div>
            {
              scannerError &&
              <button
                onClick={() => {
                  onRequestCamera(true, true);
                  trackEventsForGuests("Interacted QR code Input Screen No Camera Access", { "mp_login_user_type": "student", "Clicked on": "Allow camera access" }, { send_mixpanel: true })
                }}
                className="allow-camera-access"
              >
                Allow camera access
              </button>
            }
            {
              !scannerError && (deviceCount > 1) &&
              <button
                onClick={() => {
                  switchCamera();
                  trackEventsForGuests("Interacted QR code Input Screen Camera Access", { "mp_login_user_type": "student", "Clicked on": "Switch Camera" }, { send_mixpanel: true })
                }}
                className="allow-camera-access"
              >
                <i className="iconcust-iconSync2"></i>
                Switch Camera
              </button>
            }
            {showQRPopup ? <QRcodePopup errorInfo = {error} setLoading = {setLoading} setError = {setError}/> : null}
          </>
        )
      }
    </SwitchCamera>
  );
};
